import { MercatorCoordinate } from 'maplibre-gl';
import { cameraFollowAllowance, smoothCameraTarget } from './camera-follow';
import { journeyProgressAtFilm, type CameraPadding } from './camera-plan';
import { compileRideProgress, type RidePoint } from './ride';
import { gimbalSeconds } from '../../../renderer/src/camera-gimbal';
import type { CinematicSettings } from './cinematic-plan';

export interface KeyframeCameraFollow {
  targetAt: (offsetSeconds: number) => readonly [number, number];
  seconds: number;
  width: number;
  height: number;
  padding: CameraPadding;
}

/** Authored geometry uses the selected gimbal independently of automatic platform motion. */
export function keyframeGimbalSeconds(scene: {
  settings: CinematicSettings;
  segment?: { from: CinematicSettings; to: CinematicSettings } | undefined;
  amount: number;
  transitions?: { cameraGimbal?: { from: CinematicSettings; to: CinematicSettings; amount: number } } | undefined;
}): number {
  if (scene.transitions) {
    const transition = scene.transitions.cameraGimbal;
    return transition ? keyframeGimbalSeconds({ settings: scene.settings, segment: transition, amount: transition.amount }) : gimbalSeconds(scene.settings.cameraGimbal);
  }
  if (!scene.segment) return gimbalSeconds(scene.settings.cameraGimbal);
  const from = gimbalSeconds(scene.segment.from.cameraGimbal), to = gimbalSeconds(scene.segment.to.cameraGimbal);
  return from + (to - from) * scene.amount;
}

/** Samples only recorded positions on the film clock, within the current recording segment. */
export function keyframeCameraFollow(points: readonly RidePoint[], progress: number, duration: number,
  seconds: number, viewport: Pick<KeyframeCameraFollow, 'width' | 'height' | 'padding'>): KeyframeCameraFollow {
  const route = compileRideProgress(points), fraction = journeyProgressAtFilm(progress, duration);
  return { ...viewport, seconds, targetAt(offsetSeconds) {
    const at = journeyProgressAtFilm(Math.max(0, Math.min(1, progress + offsetSeconds / duration)), duration);
    const point = route.pointNear(at, fraction);
    return [point.longitude, point.latitude];
  } };
}

/** Damps pursuit without filtering authored rotation, tilt, altitude, or optics. */
export function smoothedKeyframeTarget(follow: KeyframeCameraFollow | undefined,
  target: readonly [number, number], zoom: number): readonly [number, number] {
  if (!follow || follow.seconds <= 0) return target;
  const current = MercatorCoordinate.fromLngLat([...target]);
  const allowance = cameraFollowAllowance(follow.width - follow.padding.left - follow.padding.right,
    follow.height - follow.padding.top - follow.padding.bottom, zoom);
  const windowSeconds = Math.max(1, 2 * follow.seconds);
  const aim = smoothCameraTarget(fraction => {
    const point = MercatorCoordinate.fromLngLat([...follow.targetAt((fraction - .5) * windowSeconds)]);
    // Unwrap around the current target so dateline crossings remain local.
    return { x: current.x + ((point.x - current.x + 1.5) % 1) - .5, y: point.y };
  }, .5, windowSeconds, allowance, follow.seconds);
  const point = new MercatorCoordinate(aim.x, aim.y).toLngLat();
  return [((point.lng + 540) % 360) - 180, point.lat];
}
